import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import clsx from 'clsx';
import {
  TrendingUp,
  AlertTriangle,
  Users,
  FileText,
  CheckCircle,
  Clock,
  DollarSign,
  Activity,
  Shield,
  Target,
  BarChart3,
  Calendar
} from 'lucide-react';
import { useAuth } from '@contexts/AuthContext';
import { dashboardService } from '@services';
import PageLayout from '@components/layout/PageLayout';
import Badge from '@components/common/Badge';
import TierBadge from '@components/common/TierBadge';
import { RZTable } from '@components/common/table';
import BarChart from '@components/charts/BarChart';
import LineChart from '@components/charts/LineChart';
import DashboardHeader from '@components/common/DashboardHeader';
import DashboardContainer from '@components/common/DashboardContainer';
import MetricsGrid from '@components/common/MetricsGrid';
import MetricCard from '@components/common/MetricCard';
import Button from '@components/common/Button';
import { Select } from '@components/common';
import ErrorState from '@components/common/ErrorState';
import ContractInfoCard from '@components/common/ContractInfoCard';
import styles from './ExecutiveDashboardPage.module.css';
import {
  TableColumn,
  RiskMetrics,
  FinancialOverview,
  TeamPerformance,
  PriorityItems,
  ComplianceMetrics
} from '@app-types';
import { statusToVariant, riskLevelToVariant, tierToVariant } from '@utils/badgeMappers';
import { formatGivenName } from '@utils/stringUtils';
import { formatMediumCalendarDate, formatTimeOnly } from '@utils/dateUtils';

const ExecutiveDashboardPage: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [selectedTab, setSelectedTab] = useState<'overview' | 'risk' | 'financial' | 'team'>('overview');
  const [selectedDivision, setSelectedDivision] = useState<string>('all'); // 'all' or division name

  // Fetch executive dashboard data
  const { data, error } = useQuery({
    queryKey: ['executive-dashboard'],
    queryFn: () => dashboardService.getExecutiveDashboard()
  });

  // Get current metrics based on selected division
  const getCurrentMetrics = () => {
    if (!data) return null;

    if (selectedDivision === 'all') {
      // Return organization-wide totals
      return {
        organization_kpis: data.organization_totals.organization_kpis,
        risk_metrics: data.organization_totals.risk_metrics,
        financial_overview: data.organization_totals.financial_overview,
        compliance_metrics: data.organization_totals.compliance_metrics,
        team_performance: data.organization_totals.team_performance,
        priority_items: data.organization_totals.priority_items,
        trending_data: data.organization_totals.trending_data
      };
    } else {
      // Return specific division metrics
      const division = data.divisions.find((d) => d.division_name === selectedDivision);
      if (!division) return null;

      return {
        organization_kpis: division.organization_kpis,
        risk_metrics: division.risk_metrics,
        financial_overview: division.financial_overview,
        compliance_metrics: division.compliance_metrics,
        team_performance: division.team_performance,
        priority_items: division.priority_items,
        trending_data: division.trending_data
      };
    }
  };

  const currentMetrics = getCurrentMetrics();

  if (error) {
    return (
      <PageLayout>
        <ErrorState
          title="Dashboard Unavailable"
          message="Unable to load executive dashboard data. Please try refreshing the page."
          action={
            <Button variant="primary" onClick={() => window.location.reload()}>
              Refresh Page
            </Button>
          }
        />
      </PageLayout>
    );
  }

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(value);
  };

  // target_date is an @db.Date calendar day — no timezone conversion.
  const formatDate = (dateString: string) => formatMediumCalendarDate(dateString);

  // Column definitions for Suppliers at Risk table
  const suppliersAtRiskColumns: TableColumn[] = [
    { key: 'supplier_name', label: 'Supplier', defaultVisible: true },
    { key: 'vendor_id', label: 'Supplier Number', defaultVisible: true },
    { key: 'tier_name', label: 'Tier', defaultVisible: true },
    { key: 'contract_count', label: 'Contracts', defaultVisible: true },
    { key: 'total_gaps', label: 'Total Gaps', defaultVisible: true },
    { key: 'high_gaps', label: 'High Priority', defaultVisible: true }
  ];

  // Column definitions for Contracts Requiring Attention table
  const contractsAttentionColumns: TableColumn[] = [
    { key: 'contract', label: 'Contract', defaultVisible: true },
    { key: 'supplier_name', label: 'Supplier', defaultVisible: true },
    { key: 'total_gap_count', label: 'Total Gaps', defaultVisible: true },
    { key: 'high_gaps', label: 'High Priority', defaultVisible: true },
    { key: 'assessment_status', label: 'Status', defaultVisible: true },
    { key: 'evidence', label: 'Evidence', defaultVisible: true }
  ];

  // Column definitions for Value Distribution Details table
  const valueDistributionColumns: TableColumn[] = [
    { key: 'tier_name', label: 'Tier', defaultVisible: true },
    { key: 'risk_level', label: 'Risk Level', defaultVisible: true },
    { key: 'contract_count', label: 'Contract Count', defaultVisible: true },
    { key: 'total_value', label: 'Total Value', defaultVisible: true },
    { key: 'avg_value', label: 'Avg Value', defaultVisible: true }
  ];

  // Render function for Suppliers at Risk table
  const renderSupplierRiskCell = (
    column: TableColumn,
    supplier: RiskMetrics['suppliers_at_risk'][number]
  ): React.ReactNode => {
    switch (column.key) {
      case 'supplier_name':
        return supplier.supplier_id ? (
          <button
            className="table-link"
            onClick={() => navigate(`/suppliers/${supplier.supplier_id}`, { state: { fromWidget: true } })}
          >
            {supplier.supplier_name}
          </button>
        ) : (
          supplier.supplier_name || '-'
        );
      case 'tier_name':
        return <TierBadge variant={tierToVariant(supplier.tier_name)}>{supplier.tier_name}</TierBadge>;
      case 'high_gaps':
        return <span className={supplier.high_gaps > 0 ? styles.highPriority : ''}>{supplier.high_gaps}</span>;
      default:
        return ((supplier as unknown as Record<string, unknown>)[column.key] as React.ReactNode) || '-';
    }
  };

  // Render function for Contracts Requiring Attention table
  const renderContractAttentionCell = (
    column: TableColumn,
    contract: RiskMetrics['contracts_requiring_attention'][number]
  ): React.ReactNode => {
    switch (column.key) {
      case 'contract':
        return (
          <ContractInfoCard
            contractNumber={contract.contract_number}
            contractName={contract.contract_name}
            className={styles.contractInfoNoMargin}
          />
        );
      case 'supplier_name':
        return contract.supplier_id ? (
          <button
            className="table-link"
            onClick={() => navigate(`/suppliers/${contract.supplier_id}`, { state: { fromWidget: true } })}
          >
            {contract.supplier_name}
          </button>
        ) : (
          contract.supplier_name || '-'
        );
      case 'high_gaps':
        return <span className={contract.high_gaps > 0 ? styles.highPriority : ''}>{contract.high_gaps}</span>;
      case 'assessment_status':
        return (
          <Badge variant={statusToVariant(contract.assessment_status as any)}>
            {contract.assessment_status || 'Unknown'}
          </Badge>
        );
      case 'evidence':
        return `${contract.evidence_approved}/${contract.evidence_provided}`;
      default:
        return ((contract as unknown as Record<string, unknown>)[column.key] as React.ReactNode) || '-';
    }
  };

  // Render function for Value Distribution Details table
  const renderValueDistributionCell = (
    column: TableColumn,
    tier: FinancialOverview['value_by_tier'][number]
  ): React.ReactNode => {
    switch (column.key) {
      case 'risk_level':
        return <Badge variant={riskLevelToVariant(tier.risk_level)}>{tier.risk_level || 'Unknown'}</Badge>;
      case 'total_value':
        return formatCurrency(tier.total_value);
      case 'avg_value':
        return formatCurrency(tier.total_value / tier.contract_count);
      default:
        return ((tier as unknown as Record<string, unknown>)[column.key] as React.ReactNode) || '-';
    }
  };

  // Column definitions for Assessor Workload table
  const assessorWorkloadColumns: TableColumn[] = [
    { key: 'assessor_name', label: 'Assessor', defaultVisible: true },
    { key: 'total_assessments', label: 'Total Assessments', defaultVisible: true },
    { key: 'completed_assessments', label: 'Final', defaultVisible: true },
    { key: 'in_progress_assessments', label: 'Active', defaultVisible: true },
    { key: 'evidence_reviewed', label: 'Evidence Reviewed', defaultVisible: true },
    { key: 'completion_rate', label: 'Completion Rate', defaultVisible: true }
  ];

  // Column definitions for Contract Manager Workload table
  const contractManagerWorkloadColumns: TableColumn[] = [
    { key: 'manager_name', label: 'Manager', defaultVisible: true },
    { key: 'total_contracts', label: 'Total Contracts', defaultVisible: true },
    { key: 'total_gaps_managed', label: 'Gaps Managed', defaultVisible: true },
    { key: 'evidence_uploaded', label: 'Evidence Uploaded', defaultVisible: true },
    { key: 'avg_gaps', label: 'Avg Gaps per Contract', defaultVisible: true }
  ];

  // Render function for Assessor Workload table
  const renderAssessorWorkloadCell = (
    column: TableColumn,
    assessor: TeamPerformance['assessor_workload'][number]
  ): React.ReactNode => {
    switch (column.key) {
      case 'completion_rate':
        return assessor.total_assessments > 0
          ? `${Math.round((assessor.completed_assessments / assessor.total_assessments) * 100)}%`
          : 'N/A';
      default:
        return ((assessor as unknown as Record<string, unknown>)[column.key] as React.ReactNode) || '-';
    }
  };

  // Render function for Contract Manager Workload table
  const renderContractManagerWorkloadCell = (
    column: TableColumn,
    manager: TeamPerformance['contract_manager_workload'][number]
  ): React.ReactNode => {
    switch (column.key) {
      case 'avg_gaps':
        return manager.total_contracts > 0 ? (manager.total_gaps_managed / manager.total_contracts).toFixed(2) : '0';
      default:
        return ((manager as unknown as Record<string, unknown>)[column.key] as React.ReactNode) || '-';
    }
  };

  // Column definitions for High Priority Gaps Without Evidence table
  const highPriorityGapsColumns: TableColumn[] = [
    { key: 'contract_number', label: 'Contract', defaultVisible: true },
    { key: 'supplier_name', label: 'Supplier', defaultVisible: true },
    { key: 'domain_name', label: 'Domain', defaultVisible: true },
    { key: 'process_name', label: 'Process', defaultVisible: true },
    { key: 'assessment_name', label: 'Assessment', defaultVisible: true }
  ];

  // Render function for High Priority Gaps Without Evidence table
  const renderHighPriorityGapsCell = (
    column: TableColumn,
    gap: PriorityItems['high_priority_gaps_without_evidence'][number]
  ): React.ReactNode => {
    switch (column.key) {
      case 'supplier_name':
        return gap.supplier_id ? (
          <button
            className="table-link"
            onClick={() => navigate(`/suppliers/${gap.supplier_id}`, { state: { fromWidget: true } })}
          >
            {gap.supplier_name}
          </button>
        ) : (
          gap.supplier_name || '-'
        );
      default:
        return ((gap as unknown as Record<string, unknown>)[column.key] as React.ReactNode) || '-';
    }
  };

  // Column definitions for Overdue Assessments table
  const overdueAssessmentsColumns: TableColumn[] = [
    { key: 'contract', label: 'Contract', defaultVisible: true },
    { key: 'supplier_name', label: 'Supplier', defaultVisible: true },
    { key: 'target_date', label: 'Target Date', defaultVisible: true },
    { key: 'assessment_status', label: 'Status', defaultVisible: true },
    { key: 'days_overdue', label: 'Days Overdue', defaultVisible: true }
  ];

  // Render function for Overdue Assessments table
  const renderOverdueAssessmentsCell = (
    column: TableColumn,
    assessment: ComplianceMetrics['overdue_assessments'][number]
  ): React.ReactNode => {
    switch (column.key) {
      case 'contract':
        return (
          <ContractInfoCard
            contractNumber={assessment.contract_number}
            contractName={assessment.contract_name}
            className={styles.contractInfoNoMargin}
          />
        );
      case 'supplier_name':
        return assessment.supplier_id ? (
          <button
            className="table-link"
            onClick={() => navigate(`/suppliers/${assessment.supplier_id}`, { state: { fromWidget: true } })}
          >
            {assessment.supplier_name}
          </button>
        ) : (
          assessment.supplier_name || '-'
        );
      case 'target_date':
        return formatDate(assessment.target_date);
      case 'assessment_status':
        return (
          <Badge variant={statusToVariant(assessment.assessment_status as any)}>
            {assessment.assessment_status || 'Unknown'}
          </Badge>
        );
      case 'days_overdue': {
        const targetDate = new Date(assessment.target_date);
        const today = new Date();
        const daysOverdue = Math.floor((today.getTime() - targetDate.getTime()) / (1000 * 60 * 60 * 24));
        return <span className="overdue-days">{daysOverdue} days</span>;
      }
      default:
        return ((assessment as unknown as Record<string, unknown>)[column.key] as React.ReactNode) || '-';
    }
  };

  return (
    <PageLayout>
      <DashboardContainer data-test-id="executive-dashboard">
        {/* Welcome Header */}
        <DashboardHeader
          title={`Welcome back, ${user ? formatGivenName(user) : ''}!`}
          subtitle="Here's your organization-wide overview and key performance indicators."
          role="Logged in as Executive"
          lastUpdated={
            data
              ? `Last updated: ${formatTimeOnly(data.last_updated, {
                  hour: 'numeric',
                  minute: '2-digit',
                  second: '2-digit'
                })}`
              : undefined
          }
        />

        {/* Tab Navigation with Division Selector */}
        <div className={styles.executiveTabsContainer}>
          <div className={styles.executiveTabs}>
            <button
              className={clsx(styles.tabButton, selectedTab === 'overview' && styles.active)}
              onClick={() => setSelectedTab('overview')}
              data-test-id="exec-tab-overview"
            >
              <Activity size={18} />
              Overview
            </button>
            <button
              className={clsx(styles.tabButton, selectedTab === 'risk' && styles.active)}
              onClick={() => setSelectedTab('risk')}
              data-test-id="exec-tab-risk"
            >
              <AlertTriangle size={18} />
              Risk Metrics
            </button>
            <button
              className={clsx(styles.tabButton, selectedTab === 'financial' && styles.active)}
              onClick={() => setSelectedTab('financial')}
              data-test-id="exec-tab-financial"
            >
              <DollarSign size={18} />
              Financial Overview
            </button>
            <button
              className={clsx(styles.tabButton, selectedTab === 'team' && styles.active)}
              onClick={() => setSelectedTab('team')}
              data-test-id="exec-tab-team"
            >
              <Users size={18} />
              Team Performance
            </button>
          </div>

          {/* Division Selector */}
          {!!data?.divisions?.length && (
            <Select
              id="division-select"
              label="Division:"
              value={selectedDivision}
              onChange={(value) => setSelectedDivision(value)}
              options={[
                { value: 'all', label: 'All Divisions' },
                ...(data?.divisions ?? []).map((division: any) => ({
                  value: division.division_name,
                  label: division.division_name
                }))
              ]}
              className={styles.divisionSelect}
              dataTestId="division-selector"
              labelPosition="left"
            />
          )}
        </div>

        {/* Overview Tab */}
        {selectedTab === 'overview' && currentMetrics && (
          <div className={styles.executiveContent}>
            {/* KPI Cards */}
            <MetricsGrid>
              <MetricCard
                icon={<Users size={24} />}
                label="Active Suppliers"
                value={currentMetrics.organization_kpis.active_suppliers || 0}
                variant="blue"
              />
              <MetricCard
                icon={<FileText size={24} />}
                label="Active Contracts"
                value={currentMetrics.organization_kpis.active_contracts || 0}
                variant="green"
              />
              <MetricCard
                icon={<CheckCircle size={24} />}
                label="Assessment Completion"
                value={`${currentMetrics.organization_kpis.assessment_completion_rate || 0}%`}
                subtitle={`${currentMetrics.organization_kpis.completed_assessments || 0} of ${currentMetrics.organization_kpis.total_assessments || 0} completed`}
                variant="purple"
              />
              <MetricCard
                icon={<AlertTriangle size={24} />}
                label="High Priority Gaps"
                value={currentMetrics.organization_kpis.high_priority_gaps || 0}
                subtitle={`${currentMetrics.organization_kpis.total_gaps_identified || 0} total gaps`}
                variant="red"
              />
            </MetricsGrid>

            {/* Evidence and Compliance Metrics */}
            <div className={styles.metricsRow}>
              <div className={styles.metricCardWide}>
                <h3>
                  <Shield size={20} /> Compliance Overview
                </h3>
                <div className={styles.metricStats}>
                  <div className={styles.statItem}>
                    <span className={styles.statLabel}>Overall Compliance Score</span>
                    <span className={styles.statValue}>
                      {currentMetrics.compliance_metrics.overall_compliance_score ?? 0}%
                    </span>
                  </div>
                  <div className={styles.statItem}>
                    <span className={styles.statLabel}>Evidence Approval Rate</span>
                    <span className={styles.statValue}>
                      {currentMetrics.organization_kpis.evidence_approval_rate || 0}%
                    </span>
                  </div>
                  <div className={styles.statItem}>
                    <span className={styles.statLabel}>Evidence Pending Review</span>
                    <span className={styles.statValue}>
                      {currentMetrics.organization_kpis.evidence_pending_review || 0}
                    </span>
                  </div>
                  <div className={styles.statItem}>
                    <span className={styles.statLabel}>Overdue Assessments</span>
                    <span className={clsx(styles.statValue, styles.statValueWarning)}>
                      {currentMetrics.compliance_metrics.overdue_assessments_count || 0}
                    </span>
                  </div>
                </div>
              </div>
            </div>

            {/* Trending Charts */}
            <div className={clsx(styles.chartsGrid, styles.trendingCharts)}>
              <div className={styles.chartCard}>
                <h3>
                  <TrendingUp size={20} /> Assessment Completion Trend
                </h3>
                {currentMetrics.trending_data.assessment_completion_trend && (
                  <LineChart
                    data={currentMetrics.trending_data.assessment_completion_trend}
                    config={{
                      title: 'Assessment Completion Trend',
                      xAxis: { column: 'month', label: 'Month' },
                      yAxis: {
                        primary: {
                          columns: ['assessments_completed', 'gaps_identified'],
                          labels: ['Final', 'Gaps Identified'],
                          format: 'number' as const
                        },
                        secondary: { columns: [], labels: [], format: 'number' as const }
                      },
                      colors: ['#4CAF50', '#FF9800'],
                      description: 'Monthly assessment completion trend'
                    }}
                    colors={['#4CAF50', '#FF9800']}
                    height={300}
                  />
                )}
              </div>
              <div className={styles.chartCard}>
                <h3>
                  <Calendar size={20} /> Evidence Submission Trend
                </h3>
                {currentMetrics.trending_data.evidence_submission_trend && (
                  <LineChart
                    data={currentMetrics.trending_data.evidence_submission_trend}
                    config={{
                      title: 'Evidence Submission Trend',
                      xAxis: { column: 'month', label: 'Month' },
                      yAxis: {
                        primary: {
                          columns: ['evidence_submitted', 'evidence_approved'],
                          labels: ['Submitted', 'Approved'],
                          format: 'number' as const
                        },
                        secondary: { columns: [], labels: [], format: 'number' as const }
                      },
                      colors: ['#2196F3', '#4CAF50'],
                      description: 'Monthly evidence submission trend'
                    }}
                    colors={['#2196F3', '#4CAF50']}
                    height={300}
                  />
                )}
              </div>
            </div>

            {/* Priority Items */}
            {currentMetrics.priority_items?.high_priority_gaps_without_evidence &&
              currentMetrics.priority_items.high_priority_gaps_without_evidence.length > 0 && (
                <div className={styles.prioritySection}>
                  <h3>
                    <Target size={20} /> High Priority Gaps Without Evidence
                  </h3>
                  <RZTable
                    data={currentMetrics.priority_items.high_priority_gaps_without_evidence}
                    columns={highPriorityGapsColumns}
                    getRowKey={(gap) => `gap-${gap.contract_number}-${gap.domain_name}-${gap.process_name}`}
                    renderCell={renderHighPriorityGapsCell}
                    state={{
                      isLoading: false,
                      isEmpty: false
                    }}
                  />
                </div>
              )}
          </div>
        )}

        {/* Risk Tab */}
        {selectedTab === 'risk' && currentMetrics && (
          <div className={styles.executiveContent}>
            <div className={styles.riskOverview}>
              <MetricsGrid>
                <MetricCard
                  icon={<AlertTriangle size={24} />}
                  label="Total Gaps"
                  value={currentMetrics.organization_kpis.total_gaps_identified || 0}
                  variant="orange"
                />
                <MetricCard
                  icon={<AlertTriangle size={24} />}
                  label="High Priority"
                  value={currentMetrics.organization_kpis.high_priority_gaps || 0}
                  variant="red"
                />
                <MetricCard
                  icon={<AlertTriangle size={24} />}
                  label="Medium Priority"
                  value={currentMetrics.organization_kpis.medium_priority_gaps || 0}
                  variant="yellow"
                />
                <MetricCard
                  icon={<CheckCircle size={24} />}
                  label="Low Priority"
                  value={currentMetrics.organization_kpis.low_priority_gaps || 0}
                  variant="blue"
                />
              </MetricsGrid>

              <div className={styles.chartsGrid}>
                <div className={styles.chartCard}>
                  <h3>
                    <BarChart3 size={20} /> Gaps by Domain
                  </h3>
                  {currentMetrics.risk_metrics.gaps_by_domain && (
                    <BarChart
                      data={currentMetrics.risk_metrics.gaps_by_domain}
                      config={{
                        title: 'Gaps by Domain',
                        xAxis: { column: 'domain_name', label: 'Domain' },
                        yAxis: {
                          primary: { columns: ['gap_count'], labels: ['Gap Count'], format: 'number' as const },
                          secondary: { columns: [], labels: [], format: 'number' as const }
                        },
                        colors: ['#FF5722'],
                        description: 'Distribution of gaps across security domains'
                      }}
                      colors={['#FF5722']}
                      height={300}
                    />
                  )}
                </div>
              </div>

              {/* Suppliers at Risk */}
              {currentMetrics.risk_metrics?.suppliers_at_risk &&
                currentMetrics.risk_metrics.suppliers_at_risk.length > 0 && (
                  <div className={styles.riskTableSection}>
                    <h3>
                      <Users size={20} /> Top Suppliers at Risk
                    </h3>
                    <RZTable
                      data={currentMetrics.risk_metrics.suppliers_at_risk}
                      columns={suppliersAtRiskColumns}
                      getRowKey={(supplier) => `supplier-${supplier.vendor_id}-${supplier.supplier_name}`}
                      renderCell={renderSupplierRiskCell}
                      state={{
                        isLoading: false,
                        isEmpty: false
                      }}
                    />
                  </div>
                )}

              {/* Contracts Requiring Attention */}
              {currentMetrics.risk_metrics?.contracts_requiring_attention &&
                currentMetrics.risk_metrics.contracts_requiring_attention.length > 0 && (
                  <div className={styles.riskTableSection}>
                    <h3>
                      <FileText size={20} /> Contracts Requiring Attention
                    </h3>
                    <RZTable
                      data={currentMetrics.risk_metrics.contracts_requiring_attention}
                      columns={contractsAttentionColumns}
                      getRowKey={(contract) => `contract-${contract.contract_number}`}
                      renderCell={renderContractAttentionCell}
                      state={{
                        isLoading: false,
                        isEmpty: false
                      }}
                    />
                  </div>
                )}
            </div>
          </div>
        )}

        {/* Financial Tab */}
        {selectedTab === 'financial' && currentMetrics && (
          <div className={styles.executiveContent}>
            <div className={styles.financialOverview}>
              <MetricsGrid>
                <MetricCard
                  icon={<DollarSign size={24} />}
                  label="Total Contract Value"
                  value={formatCurrency(currentMetrics.financial_overview.total_contract_value || 0)}
                  subtitle={`${currentMetrics.financial_overview.total_contracts || 0} contracts`}
                  variant="green"
                />
                <MetricCard
                  icon={<CheckCircle size={24} />}
                  label="Active Contract Value"
                  value={formatCurrency(currentMetrics.financial_overview.active_contract_value || 0)}
                  subtitle={`${currentMetrics.financial_overview.active_contracts || 0} active`}
                  variant="blue"
                />
              </MetricsGrid>

              <div className={styles.chartsGrid}>
                <div className={styles.chartCard}>
                  <h3>
                    <BarChart3 size={20} /> Contract Value by Tier
                  </h3>
                  {currentMetrics.financial_overview.value_by_tier && (
                    <BarChart
                      data={currentMetrics.financial_overview.value_by_tier}
                      config={{
                        title: 'Contract Value by Tier',
                        xAxis: { column: 'tier_name', label: 'Tier' },
                        yAxis: {
                          primary: { columns: ['total_value'], labels: ['Total Value'], format: 'currency' as const },
                          secondary: { columns: [], labels: [], format: 'number' as const }
                        },
                        colors: ['#4CAF50'],
                        description: 'Total contract value by supplier tier'
                      }}
                      colors={['#4CAF50']}
                      height={300}
                    />
                  )}
                </div>
                {/* Only show Contract Value by Division when viewing all divisions */}
                {selectedDivision === 'all' && (
                  <div className={styles.chartCard}>
                    <h3>
                      <BarChart3 size={20} /> Contract Value by Division
                    </h3>
                    {currentMetrics.financial_overview.value_by_division && (
                      <BarChart
                        data={currentMetrics.financial_overview.value_by_division}
                        config={{
                          title: 'Contract Value by Division',
                          xAxis: { column: 'division_name', label: 'Division' },
                          yAxis: {
                            primary: { columns: ['total_value'], labels: ['Total Value'], format: 'currency' as const },
                            secondary: { columns: [], labels: [], format: 'number' as const }
                          },
                          colors: ['#2196F3'],
                          description: 'Total contract value by business division'
                        }}
                        colors={['#2196F3']}
                        height={300}
                      />
                    )}
                  </div>
                )}
              </div>

              {/* Financial Details Table */}
              {currentMetrics.financial_overview.value_by_tier && (
                <div className={styles.financialTableSection}>
                  <h3>
                    <DollarSign size={20} /> Value Distribution Details
                  </h3>
                  <RZTable
                    data={currentMetrics.financial_overview.value_by_tier}
                    columns={valueDistributionColumns}
                    getRowKey={(tier) => `tier-${tier.tier_name}`}
                    renderCell={renderValueDistributionCell}
                    state={{
                      isLoading: false,
                      isEmpty: false
                    }}
                  />
                </div>
              )}
            </div>
          </div>
        )}

        {/* Team Performance Tab */}
        {selectedTab === 'team' && currentMetrics && (
          <div className={styles.executiveContent}>
            <div className={styles.teamOverview}>
              <MetricsGrid>
                <MetricCard
                  icon={<Clock size={24} />}
                  label="Avg Completion Time"
                  value={`${currentMetrics.team_performance.avg_assessment_completion_days || 0} days`}
                  variant="purple"
                />
                <MetricCard
                  icon={<Users size={24} />}
                  label="Active Assessors"
                  value={currentMetrics.team_performance?.assessor_workload?.length || 0}
                  variant="blue"
                />
                <MetricCard
                  icon={<Users size={24} />}
                  label="Active Managers"
                  value={currentMetrics.team_performance?.contract_manager_workload?.length || 0}
                  variant="green"
                />
              </MetricsGrid>

              {/* Assessor Workload */}
              {currentMetrics.team_performance?.assessor_workload &&
                currentMetrics.team_performance.assessor_workload.length > 0 && (
                  <div className={styles.teamTableSection}>
                    <h3>
                      <Users size={20} /> Assessor Workload
                    </h3>
                    <RZTable
                      data={currentMetrics.team_performance.assessor_workload}
                      columns={assessorWorkloadColumns}
                      getRowKey={(assessor) => `assessor-${assessor.id}`}
                      renderCell={renderAssessorWorkloadCell}
                      state={{
                        isLoading: false,
                        isEmpty: false
                      }}
                    />
                  </div>
                )}

              {/* Contract Manager Workload */}
              {currentMetrics.team_performance?.contract_manager_workload &&
                currentMetrics.team_performance.contract_manager_workload.length > 0 && (
                  <div className={styles.teamTableSection}>
                    <h3>
                      <Users size={20} /> Contract Manager Workload
                    </h3>
                    <RZTable
                      data={currentMetrics.team_performance.contract_manager_workload}
                      columns={contractManagerWorkloadColumns}
                      getRowKey={(manager) => `manager-${manager.id}`}
                      renderCell={renderContractManagerWorkloadCell}
                      state={{
                        isLoading: false,
                        isEmpty: false
                      }}
                    />
                  </div>
                )}
            </div>
          </div>
        )}

        {/* Overdue Assessments Footer */}
        {currentMetrics &&
          currentMetrics.compliance_metrics.overdue_assessments.length > 0 &&
          selectedTab === 'overview' && (
            <div className={clsx(styles.overdueSection, styles.overdueSectionCompact)}>
              <h3>
                <Clock size={18} /> Overdue Assessments
              </h3>
              <RZTable
                data={currentMetrics.compliance_metrics.overdue_assessments}
                columns={overdueAssessmentsColumns}
                getRowKey={(assessment) => `overdue-${assessment.contract_number}-${assessment.target_date}`}
                renderCell={renderOverdueAssessmentsCell}
                state={{
                  isLoading: false,
                  isEmpty: false
                }}
              />
            </div>
          )}
      </DashboardContainer>
    </PageLayout>
  );
};

export default ExecutiveDashboardPage;
